/**
 * One vocabulary for reporting rows the exchange itself reported as cancelled.
 *
 * WHY THIS EXISTS — the bug it closes:
 * a `RETIRO CANCELADO` / `Buy - Failed` row is WITHHELD ON PURPOSE (#692): booking it would
 * invent a taxable event that never happened. #698 gave the custom-importer wizard copy that says
 * so — but the copy AND the count-splitting lived inside that one hook, so every other surface
 * kept reporting the same rows as a plain failure. Through the "Añadir CSV" modal the import runs
 * as a background job and reports via the post-sync summary, which said only
 * "✗ No procesables: 4" next to "el equipo está trabajando en ello" — both false, and the user
 * re-uploads the file hunting for data that was never supposed to land.
 *
 * The decision lives here so every surface reaches the SAME words from the SAME counts. A second
 * set of strings, or a second definition of "cancelled", would drift the moment either is touched.
 */

/** Backend error code for a row the exchange reported as cancelled/failed/rejected. */
const CANCELLED_OPERATION_ERROR_CODE = 'CANCELLED_OPERATION'

/** The counts every import surface can produce, whatever shape its raw result has. */
export interface ImportOutcomeCounts {
  /** Rows that landed in the ledger. */
  readonly importedCount: number
  /** Rows withheld on purpose because the exchange said the operation never happened. */
  readonly cancelledCount: number
  /** Rows that genuinely did not make it — rejected, unreadable, unclassifiable. */
  readonly failedCount: number
  /**
   * Rows the user may still have to look at for some OTHER reason (skipped as duplicate, waiting
   * in the needs-attention tray). They are not failures, but their presence means a withholding
   * is not the whole story — so "nothing is missing" would be a lie. Defaults to 0.
   */
  readonly otherReportedCount?: number
}

export interface CancelledImportOutcome {
  /** i18n key, in the `imports` namespace (the copy #698 already shipped). */
  readonly key: string
  readonly params: Record<string, number>
  /**
   * `info` when nothing needs fixing (only withheld rows); `warning` only when REAL failures are
   * part of the same batch. A withholding announced as a warning is the original bug.
   */
  readonly severity: 'info' | 'warning'
}

/** How many of a batch's errors are deliberate withholdings rather than real rejections. */
export function countCancelledImportErrors(
  errors: readonly { readonly code?: string | undefined }[],
): number {
  return errors.filter((error) => error.code === CANCELLED_OPERATION_ERROR_CODE).length
}

/**
 * The message for a batch that contains withheld rows, or `null` when it contains none — in which
 * case the caller keeps whatever copy it already uses for duplicates / partial / failed imports.
 * Returning `null` (rather than a "nothing cancelled" variant) is what keeps this from becoming a
 * second, competing description of a normal import.
 */
export function resolveCancelledImportOutcome(
  counts: ImportOutcomeCounts,
): CancelledImportOutcome | null {
  if (counts.cancelledCount <= 0) return null

  if (counts.failedCount > 0) {
    // Mixed batch: real rejections happened too, so each count is reported separately — the
    // cancelled rows must never inflate (or hide) the number that genuinely could not be saved.
    return {
      key: 'customImporter.notifications.importWithErrorsAndCancelled',
      params: {
        count: counts.importedCount,
        failed: counts.failedCount,
        cancelled: counts.cancelledCount,
      },
      severity: 'warning',
    }
  }

  if (counts.importedCount === 0) {
    // Only withheld rows, and nothing landed: informative, nothing to fix, nothing missing.
    return {
      key: 'customImporter.notifications.importAllCancelled',
      params: { count: counts.cancelledCount },
      severity: 'info',
    }
  }

  return {
    key: 'customImporter.notifications.importWithCancelled',
    params: { count: counts.importedCount, cancelled: counts.cancelledCount },
    severity: 'info',
  }
}

/**
 * True when the import wrote nothing AND the withholding is the WHOLE story — no failures and
 * nothing else awaiting the user.
 *
 * This is the case a generic "finished but created 0 transactions" warning gets wrong: the import
 * is complete and correct, so the surface should explain the withholding instead of alarming. The
 * `otherReportedCount` guard is what stops it over-reaching: a batch that also skipped duplicates
 * or parked rows in the tray still owes the user the generic warning.
 */
export function isImportOutcomeFullyWithheld(counts: ImportOutcomeCounts): boolean {
  return (
    counts.importedCount === 0 &&
    counts.cancelledCount > 0 &&
    counts.failedCount === 0 &&
    (counts.otherReportedCount ?? 0) === 0
  )
}
